import React from 'react';
import PropTypes from 'prop-types';
import { SFUpcomingAppt, SFEntityID, SFNotification } from 'utils/types';
import { fetchAuthenticatedJSON, apiErrorMessage, urlParams } from 'utils/request';
import css from './EditAppointment.css';
import Main from './Main';
import CoursesView from 'serviceCatalog/appointments/course/CoursesView';
import moment from 'moment';
import 'moment-timezone';
import { isEmpty } from 'lodash';
import Footer from 'common/Footer';
import StyledButton from 'common/StyledButton';

export default class EditAppointment extends React.Component {

  constructor(props) {
    super(props);
    const { description, courseContext, location } = props.appointment;
    this.state = {
      content: "MAIN",
      description,
      courseContextId: isEmpty(courseContext) ? 0 : courseContext.id,
      potentialCourseContextId: 0,
      locationId: location.id,
      saving: false, // Determines if user is saving changes
      hasChanged: false // Determine if the user has changed any value
    };

    this.onBackButtonClicked = this.onBackButtonClicked.bind(this);
  }

  get courseName() {
    const { potentialCourseContexts } = this.props.appointment;

    // If we have a course context but no potential courses to change, just display its name
    if ( isEmpty(potentialCourseContexts) && !isEmpty(this.props.appointment.courseContext) ) {
      return this.props.appointment.courseContext.name;
    }

    // If we have selected a courseContextId and we have potential courses, find the name in the dict
    if ( this.state.courseContextId && !isEmpty(potentialCourseContexts) ) {
      return potentialCourseContexts.find(({id}) => parseInt(id) === this.state.courseContextId).name;
    }

    // Otherwise just don't display any name
    return "";
  }

  get locationProps() {
    const { potentialLocations } = this.props.appointment;

    if ( isEmpty(potentialLocations) && !isEmpty(this.props.appointment.location) ) {
      return { name: this.props.appointment.location.name, instructions: this.props.appointment.location.instructions }
    }

    if ( this.state.locationId && !isEmpty(potentialLocations) ) {
      const matchedLocation = potentialLocations.find(({id}) => parseInt(id) === this.state.locationId);
      return { name: matchedLocation.name, instructions: matchedLocation.instructions };
    }

    // Shouldn't reach this since a location should always be set.
    return { name: "", instructions: "" };
  }

  get main() {
    return (
      <Main
        appointmentId={this.props.appointment.id}
        ownerName={this.props.appointment.owner.name}
        startDate={moment(this.props.appointment.startDate).tz(this.props.timeZone)}
        duration={this.props.appointment.duration}
        recurring={this.props.appointment.recurring}
        notification={this.props.notification}
        onChangeDurationClicked={() => this.setState({content: "DURATION"})}
        locations={this.props.appointment.potentialLocations}
        locationId={this.state.locationId}
        locationName={this.locationProps.name}
        canChangeLocation={!isEmpty(this.props.appointment.potentialLocations)}
        onLocationSelected={this.onLocationSelected.bind(this)}
        instructions={this.locationProps.instructions}
        reasonName={this.props.appointment.appointmentReason}
        details={this.state.description}
        participants={this.props.appointment.scheduleBlock.groupSessionParticipants}
        onDetailsChanged={e => this.setState({description: e.target.value, hasChanged: true})}
        courseName={this.courseName}
        canChangeCourse={!isEmpty(this.props.appointment.potentialCourseContexts)}
        onChangeCourseClicked={() => this.setState({content: "COURSE"})}
        isCourseClearable={!this.props.appointment.scheduleBlock.supplementalInstruction}
        onClearCourseClicked={() => this.setState(
          {courseContextId: 0, potentialCourseContextId: 0, hasChanged: true}
        )}
        scheduleBlockInstructions={this.props.appointment.scheduleBlock.instructions}
      />
    );
  }

  get course() {
    const { potentialCourseContextId, courseContextId } = this.state;
    return (
      <CoursesView
        courseContextId={potentialCourseContextId ? potentialCourseContextId : courseContextId}
        onCourseSelected={(newCourseContextId) => this.setState({potentialCourseContextId: parseInt(newCourseContextId)})}
        courseOptions={this.props.appointment.potentialCourseContexts}
      />
    );
  }

  onLocationSelected(newLocationId) {
    this.setState({locationId: parseInt(newLocationId), hasChanged: true})
  }

  get content() {
    switch ( this.state.content ) {
      case "MAIN":
        return this.main;
      case "COURSE":
        return this.course;
      default:
        return null;
    }
  }

  onBackButtonClicked(e) {
    if ( e ) e.preventDefault();
    switch ( this.state.content ) {
      case "MAIN":
        window.location.href = "#"; // Go back to upcoming screen
        break;
      default:
        // Resets any changes not saved and go back to main screen
        this.setState({
          content: "MAIN",
          potentialCourseContextId: 0
        });
        break;
    }
    return false; // Avoid anchor tag redirection
  }

  onContinueButtonClicked() {
    switch ( this.state.content ) {
      case "MAIN":
        this.saveAppointment();
        break;
      case "COURSE":
        this.onSaveButtonClicked('courseContextId', this.state.potentialCourseContextId);
        break;
      default:
        break;
    }
  }

  onSaveButtonClicked(attr, value) {
    this.setState({[attr]: value, hasChanged: true, content: "MAIN" });
  }

  saveAppointment() {
    this.setState({saving: true});
    const URL = "../appointments/saveAppointment.html";
    return fetchAuthenticatedJSON(URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
      },
      body: urlParams({
        meetingType: "Appointment",
        appId: this.props.appointment.id,
        appointmentTypeReasonId: this.props.appointment.appointmentTypeReasonId,
        scheduleBlockId: this.props.appointment.scheduleBlock.id,
        startDate: moment(this.props.appointment.startDate).tz(this.props.timeZone).format("YYYY-MM-DD"),
        startTime: moment(this.props.appointment.startDate).tz(this.props.timeZone).format("HH:mm"),
        partyId: this.props.partyId,
        description: this.state.description,
        duration: this.props.appointment.duration,
        locationId: this.state.locationId,
        courseContext: this.state.courseContextId
      })
    })
    .then(() => {
      window.location.href = "#"; // Redirect to upcoming home
      this.props.notification.success("Appointment successfully edited"); // All good
    })
    .catch(e => this.props.notification.error(apiErrorMessage(e)))
    .then(() => this.setState({saving: false}));
  }

  get canClickOnContinueButton() {
    switch ( this.state.content ) {
      case "MAIN":
        // On main screen, only allow clicking if some value has changed we are not saving already
        return this.state.hasChanged && !this.state.saving;
      case "COURSE":
        // On course screen, only allow save if you have selected any course
        return this.state.potentialCourseContextId !== 0;
      default:
        return false;
    }
  }

  get continueButtonText() {
    if ( this.state.saving ) return "Saving...";
    if ( this.state.content === "MAIN" ) return "Save Changes";
    else return "Save";
  }

  render() {
    return (
      <div className={css['container']}>
        {this.props.header}
        <div className={css['content']}>{this.content}</div>
        <Footer
          primaryButton={
            <StyledButton
              disabled={!this.canClickOnContinueButton}
              onClick={this.onContinueButtonClicked.bind(this)}
            >{this.continueButtonText}</StyledButton>
          }
          secondaryButton={
            <StyledButton asLink onClick={this.onBackButtonClicked}>Back</StyledButton>
          }
        />
      </div>
    );
  }
}

EditAppointment.propTypes = {
  appointment: PropTypes.shape(SFUpcomingAppt),
  header: PropTypes.object.isRequired,
  partyId: SFEntityID.isRequired,
  notification: SFNotification.isRequired,
  timeZone: PropTypes.string.isRequired
};
